<template>
  <div class="ordersHistory">
    <Loading v-if="loading" />
    <Title :title="$t('ordersHistory')" />
    <div class="ordersHistory__header">
      <div class="ordersHistory__header-search">
        <img
          class="dropdown-img"
          src="@/assets/icons/Search.svg"
          alt="Search"
        />
        <input
          v-model="search"
          class="input-search"
          :placeholder="$t('search')"
          @input="changeInput"
        />
        <Dropdown
          class-name="dropdown-search"
          :selected-option="getSearchBy.selectedOption"
          :options="getSearchBy.options"
          @select-option="changeSearchBy"
        />
      </div>
      <div class="ordersHistory__header-chooseDate">
        <Button
          class="getCSV-button"
          variant="bordered"
          :title="$t('downloadPDF')"
          @click="getPDF"
        />
        <div class="chooseDate-date">
          <div class="chooseDate-date-inputs">
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                data-testid="calendarStartTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                data-testid="calendarEndTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
          </div>
          <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
        </div>
      </div>
    </div>
    <div v-if="isDateCorrect && ordersHistoryList.length">
      <Table
        :data="ordersHistoryList"
        :columns="columns"
        selected-field="createdTime"
        selected-view-order="id"
        @get-ssn="getSsn"
        @go-to-item="openViewOrder"
        @open-confirmation="openConfirmation"
        @quick-reservation="quickReservation"
        @add-description="addDescription"
      />
    </div>
    <div
      v-if="!isDateCorrect || !ordersHistoryList.length"
      class="ordersHistory__noReserve"
    >
      <p>{{ $t('noData') }}</p>
    </div>
  </div>
  <OpenConfirmation
    :show-open-confirmation="showOpenConfirmation"
    @close-modal="showOpenConfirmation = false"
    @confirm-request="confirmRequest"
  />
  <AddComment
    :show-modal-add-comment="showModalAddComment"
    :active-payment="activePayment"
    @close-modal="closeModalAddComment"
  />
  <QuickReservation
    v-if="showModalQuickReservation"
    :show-modal-quick-reservation="showModalQuickReservation"
    :view-order-data="viewOrder"
    @close-modal="closeModalQuickReservation"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import router from '@/router';
import { format } from 'date-fns';

import Loading from '@/views/Loading.vue';
import OpenConfirmation from './OpenConfirmation/OpenConfirmation.vue';
import AddComment from './AddComment/AddComment.vue';
import QuickReservation from '@/duplicatePages/QuickReservation/QuickReservation.vue';

import {
  getConsultation,
  getDeliveryMethod,
  getPaymentStatus,
  getContent,
  dateTimeResult
} from '@/utils/tableFunctions';

import { columns, columnsDow, searchActions } from './configs';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './DejaVuSans-bold.js';

import { InputFileEvent, Option } from '@/types';
import { OrderHistory } from '@/store/modules/orders/types';
import { Params } from '@/store/modules/users/types';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'OrdersHistory',
  components: {
    Loading,
    OpenConfirmation,
    AddComment,
    QuickReservation
  },
  data() {
    return {
      loading: false,
      showOpenConfirmation: false,
      showOpenViewOrder: false,
      showModalAddComment: false,
      showModalQuickReservation: false,
      search: '',
      viewOrder: null,
      getSearchBy: {
        selectedOption: {
          value: 'firstName',
          label: this.$t('firstName')
        },
        options: searchActions(this.$t)
      },
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      ordersHistoryList: [],
      columns: columns(this.$t),
      columnsDow: columnsDow(this.$t),
      activePayment: {},
      timestampActivePayment: 0,
      dataSearch: [],
      showTimePanel: false,
      buttonLoaded: false
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('orders', ['ordersHistory', 'decodedSSN']),
    isDateCorrect() {
      if (this.isDatePresent) {
        const startTime = this.dateRange.startTime.split('/');
        const endTime = this.dateRange.endTime.split('/');

        const defaultParams = {
          ...this.dateRange,
          startTime: new Date(
            +startTime[2],
            startTime[1] - 1,
            +startTime[0]
          ).valueOf(),
          endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
        };

        return defaultParams.endTime >= defaultParams.startTime;
      }

      return true;
    },
    isDatePresent() {
      const startTime = this.dateRange.startTime.split('/');
      const endTime = this.dateRange.endTime.split('/');

      const defaultParams = {
        ...this.dateRange,
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
      };

      return defaultParams && defaultParams.startTime && defaultParams.endTime;
    }
  },
  created() {
    if (this.user) {
      this.pharmacyId = this.user?.pharmacyId;
      this.show();
    }
  },
  methods: {
    ...mapActions('orders', [
      'getOrdersHistory',
      'getConfirmContent',
      'getOrdersHistoryDecodedSSN',
      'getViewOrderById'
    ]),
    show() {
      let startTime = this.dateRange.startTime.split('/');
      let endTime = this.dateRange.endTime.split('/');

      startTime = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();

      const today = new Date().getDate();

      if (today === +endTime[0]) {
        endTime = new Date().valueOf();
      } else {
        endTime = new Date(
          +endTime[2],
          endTime[1] - 1,
          +endTime[0],
          23,
          59,
          59
        ).valueOf();
      }

      this.params = {
        startTime: startTime + new Date(startTime).getTimezoneOffset() * -60000,
        endTime: endTime + new Date(endTime).getTimezoneOffset() * -60000,
        pharmacyId: this.user.pharmacyId
      };

      this.getOrdersHistoryList(this.params);
    },
    async getOrdersHistoryList(params: Params) {
      if (
        params.pharmacyId &&
        params.startTime &&
        params.endTime &&
        this.isDateCorrect
      ) {
        this.loading = true;

        await this.getOrdersHistory(params);
        this.ordersHistoryList = this.ordersHistory;

        this.loading = false;
      }
    },
    closeModalAddComment() {
      this.showModalAddComment = false;
      this.show();
    },
    closeModalQuickReservation() {
      this.showModalQuickReservation = false;
      this.show();
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;
      this.buttonLoaded = false;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };

      this.buttonLoaded = true;
    },
    closeTimePanel() {
      this.show();
      this.showTimePanel = false;

      this.buttonLoaded = false;
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (value === '') {
          this.ordersHistoryList = this.ordersHistory;
        } else if (this.getSearchBy.selectedOption.value === 'firstName') {
          this.ordersHistoryList = [
            ...(Object.values(this.ordersHistory) as OrderHistory[]).flat()
          ].filter((item: OrderHistory) =>
            item.clientInfo.firstName
              .toLowerCase()
              .includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'lastName') {
          this.ordersHistoryList = [
            ...(Object.values(this.ordersHistory) as OrderHistory[]).flat()
          ].filter((item: OrderHistory) =>
            item.clientInfo.lastName
              .toLowerCase()
              .includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'phoneNumber') {
          this.ordersHistoryList = [
            ...(Object.values(this.ordersHistory) as OrderHistory[]).flat()
          ].filter((item: OrderHistory) =>
            item.clientInfo.phoneNumber
              .toLowerCase()
              .includes(value?.toLowerCase())
          );
        }
      }, 600);
    },
    changeSearchBy(option: readonly Option[]) {
      this.getSearchBy.selectedOption = option;

      this.ordersHistoryList = [
        ...(Object.values(this.ordersHistory) as OrderHistory[]).flat()
      ].filter((item: OrderHistory) =>
        (
          item[
            this.getSearchBy.selectedOption.value as keyof OrderHistory
          ] as string
        )
          ?.toLowerCase()
          .includes(this.search.toLowerCase())
      );

      if (this.search === '') {
        this.ordersHistoryList = this.ordersHistory;
      }
    },
    getActivePayment(createdTime: number) {
      this.activePayment = [
        ...(Object.values(this.ordersHistory) as OrderHistory[])
      ].find((i: OrderHistory) => i.createdTime === createdTime);
    },
    async getSsn(createdTime: string) {
      this.getActivePayment(createdTime);

      const params: Params = {
        pharmacyId: this.user?.pharmacyId,
        orderId: this.activePayment.id
      };

      this.loading = true;

      await this.getOrdersHistoryDecodedSSN(params);

      if (this.activePayment) {
        this.activePayment.clientInfo.ssn = this.decodedSSN.ssn;
      }

      this.loading = false;
    },
    openConfirmation(createdTime: number) {
      this.getActivePayment(createdTime);
      this.timestampActivePayment = this.activePayment.createdTime;

      this.showOpenConfirmation = true;
    },
    addDescription(createdTime: number) {
      this.getActivePayment(createdTime);
      this.showModalAddComment = true;
    },
    async quickReservation(id: string) {
      await this.getViewOrderById({
        pharmacyId: this.pharmacyId,
        orderId: id
      });

      this.viewOrder = this.ordersHistoryList.find(
        (i: OrderHistory) => i.id === id
      );

      this.showModalQuickReservation = true;
    },
    async confirmRequest() {
      await this.getConfirmContent({
        pharmacyId: this.user?.pharmacyId,
        orderId: this.activePayment.id
      });

      this.showOpenConfirmation = false;
      this.show();
    },
    openViewOrder(id: string) {
      router.push({
        name: 'ViewOrder',
        params: { id, pharmacyId: this.user?.pharmacyId }
      });
    },
    getContentResult(data: OrderHistory) {
      if (data.confirmed) {
        return `${getContent(data)} ${dateTimeResult(data?.confirmedTime)}`;
      } else {
        return '-';
      }
    },
    getPDF() {
      const data = this.ordersHistoryList.map((el: OrderHistory) => ({
        consentGiven: '☑',
        clientInfoName: `${el.clientInfo.firstName} ${el.clientInfo.lastName}`,
        phoneNumber: el.clientInfo.phoneNumber,
        handler: el.handler,
        consultation: getConsultation(el),
        orderNumber: el.orderNumber,
        createdTime: dateTimeResult(el.createdTime),
        delivery: getDeliveryMethod(el.deliveryMethod),
        content: this.getContentResult(el),
        total: el.paymentInfo?.totalSum,
        payment: getPaymentStatus(el.paymentInfo?.status),
        timestamp: dateTimeResult(el.paymentInfo?.issuedAt),
        comment: el.comment
      }));

      const dataPDF = data.map((obj: OrderHistory) => Object.values(obj));

      const columns = [...this.columnsDow].map((i: Option) => i.label);

      const doc = new jsPDF('landscape');

      doc.setFont('DejaVuSans');

      autoTable(doc, {
        head: [columns],
        body: dataPDF,
        theme: 'grid',
        headStyles: {
          fillColor: [71, 152, 127],
          fontSize: 7,
          halign: 'center'
        },
        bodyStyles: { fillColor: [255, 255, 255] },
        styles: {
          overflow: 'linebreak',
          fontSize: 7
        },
        margin: { top: 10 },
        didParseCell: function (data) {
          if (data.column.index === 0) {
            data.cell.styles.font = 'DejaVuSans';
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      doc.save(`${this.$t('ordersHistory')}.pdf`);
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.pharmacyId = this.user?.pharmacyId;
        this.show();
      }
    }
  }
})
export default class OrdersHistory extends Vue {}
</script>

<style lang="scss">
.ordersHistory {
  display: flex;
  flex-direction: column;
  padding: 24px;
  .ordersHistory__header {
    display: flex;
    justify-content: space-between;
    padding-top: 32px;
    padding-bottom: 10px;
    @media screen and (max-width: 1220px) {
      flex-direction: column;
    }
    .ordersHistory__header-search {
      position: relative;
      display: flex;
      margin-right: 10px;
      @media screen and (max-width: 1350px) {
        margin-bottom: 24px;
      }
      .input-search {
        width: 245px;
        height: 38px;
        padding-left: 36px;
        font-size: $fourteenSize;
        border: 1px solid $border;
        border-right: 0;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
        outline: none;
      }
      .dropdown-img {
        position: absolute;
        top: 14px;
        left: 17px;
      }
      .dropdown {
        max-width: 150px;
        .dropdown-search {
          height: 40px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }
    }
    .ordersHistory__header-chooseDate {
      display: flex;
      justify-content: flex-start;
      @media screen and (max-width: 1350px) {
        justify-content: flex-end;
      }
      .chooseDate-date {
        height: 100%;
        margin-left: 10px;
        .chooseDate-date-inputs {
          display: flex;
          align-items: center;
          .chooseDate-button {
            width: 60px;
            margin-right: 10px;
            z-index: 101;
            cursor: pointer;
          }
          .input {
            margin-bottom: 0;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
}
</style>
